import { HttpHeaders, HttpRequest, HttpResponse } from "@angular/common/http";
import { API_URLS, DebuggingSettings } from "blu-utilities";

export type ExpectedValues = Record<string, string>;

export function constructCurlCommand(
  request: HttpRequest<any>,
  debuggingSettings?: DebuggingSettings,
): string {
  let curlCommand = "curl";
  // Append headers
  request.headers.keys().forEach((header) => {
    // Skip Authorization header if includeTokenInRequest is true
    if (header.toLowerCase() !== "authorization") {
      curlCommand += ` --header '${header}: ${request.headers.get(header)}'`;
    }
  });

  // Append method
  curlCommand += ` -X ${request.method}`;

  // Append Authorization header with Bearer token or a placeholder
  if (debuggingSettings?.includeTokenInRequest) {
    const bearerToken = request.headers.get("Authorization");
    if (bearerToken) {
      curlCommand += ` --header 'Authorization: ${bearerToken}'`;
    }
  }

  const baseUrl = debuggingSettings?.baseUrl ?? "baseUrl";
  // Append request URL
  curlCommand += ` '${request.urlWithParams.replace(API_URLS.PLATFORM, baseUrl)}'`;

  // Append Content-Type header for JSON requests
  if (request.body && !(request.body instanceof FormData)) {
    curlCommand += " --header 'Content-Type: application/json'";
  }

  // Append request body if exists
  if (
    request.body instanceof FormData &&
    typeof request.body.entries === "function"
  ) {
    // If the body is FormData, iterate over its entries
    for (const [key, value] of request.body.entries()) {
      curlCommand += ` -F '${key}=${value}'`;
    }
  } else if (request.body) {
    // If the body is not FormData, treat it as a regular JSON object
    const body =
      typeof request.body === "string"
        ? request.body
        : JSON.stringify(request.body).replace(/'/g, "\\'");
    curlCommand += ` --data-raw '${body}'`;
  }

  return curlCommand;
}

export function getHeadersObject(headers: HttpHeaders): Record<string, string> {
  const headersObject: Record<string, string> = {};
  headers.keys().forEach((header) => {
    headersObject[header] = headers.get(header) || "";
  });
  return headersObject;
}

export function generatePostmanTestScripts(
  response: HttpResponse<any>,
  request?: HttpRequest<any>,
): string {
  let statusTest = "";
  let expectedValuesTest = "";

  // Test the status of the response
  if (request?.method === "GET") {
    statusTest = getPmTestString(200);
    expectedValuesTest = getExpectedValuesTest(response?.body?.data);
  } else if (request?.method === "POST") {
    statusTest = getPmTestString(201);
    expectedValuesTest = getExpectedValuesTest(response?.body?.data);
  } else if (request?.method === "PUT") {
    statusTest = getPmTestString(202);
  } else if (request?.method === "DELETE") {
    statusTest = getPmTestString(204);
  }

  return `
${statusTest}
${expectedValuesTest}
`;
}

function getExpectedValuesTest(jsonData: any): string {
  if (jsonData) {
    const dataKeys =
      Array.isArray(jsonData) && jsonData[0]
        ? Object.keys(jsonData[0])
        : Object.keys(jsonData);

    // Use the keys for dynamic expected values
    const expectedValues = dataKeys.reduce((acc, key) => {
      acc[key] = jsonData[key] || ""; // Assign the actual value from the response
      return acc;
    }, {} as ExpectedValues);
    const expectedValuesString = JSON.stringify(expectedValues, null, 2);

    return `
// Test if the response has correct values \n
pm.test("Response body has correct values", function () {
    var jsonData = pm.response.json();
    var expectedValues = ${expectedValuesString};
    pm.expect(jsonData.data).to.deep.include(expectedValues);
});
`;
  }

  // If jsonData is null or undefined, return an empty string
  return "";
}

export function getPmTestString(statusCode: number) {
  return `// Test that the request has the expected status code \n pm.test("Status code is ${statusCode}", function () {\n    pm.response.to.have.status(${statusCode});\n});`;
}
